import { Component, forwardRef, inject, Input, OnInit } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  Validator,
  Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';

import { TimePickerComponent } from '../time-picker';
import { DEFAULT_TIME_INTERVAL, TIME_FORMAT } from '../utils';

export interface TimeRange {
  from: string;
  to: string;
}

@Component({
  selector: 'rms-timerange',
  imports: [ReactiveFormsModule, MatFormFieldModule, MatIconModule, TimePickerComponent, TranslateModule],
  templateUrl: './timerange.component.html',
  styleUrl: './timerange.component.scss',
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => TimerangeComponent), multi: true },
    { provide: NG_VALIDATORS, useExisting: forwardRef(() => TimerangeComponent), multi: true },
  ],
})
export class TimerangeComponent implements ControlValueAccessor, Validator, OnInit {
  @Input() public interval = DEFAULT_TIME_INTERVAL;

  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = new Subject<void>();

  public form = this.fb.group(
    {
      from: this.fb.control<string>('', {
        validators: [Validators.required],
      }),
      to: this.fb.control<string>('', {
        validators: [Validators.required],
      }),
    },
    { validators: [this.rangeValidator()] }
  );

  private onChange: (value: TimeRange | null) => void = () => {};
  public onTouched: () => void = () => {};

  ngOnInit(): void {
    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value) => {
        this.onTouched();
        const mapped = value.from && value.to ? (value as TimeRange) : null;
        this.onChange(mapped);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public swapValues(): void {
    const value = this.form.value;
    this.form.patchValue({
      from: value.to,
      to: value.from,
    });
  }

  writeValue(value: TimeRange | null): void {
    if (!value) {
      return;
    }

    const local = this.form.value;
    if (value.from !== local.from || value.to !== local.to) {
      this.form.patchValue(value, { emitEvent: false });
    }
  }

  registerOnChange(fn: (value: TimeRange | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  validate(_control: AbstractControl): ValidationErrors | null {
    if (this.form.errors) {
      return this.form.errors;
    }
    return this.form.valid ? null : { invalid: true };
  }

  private rangeValidator() {
    return (group: AbstractControl) => {
      if (group === null || group.value.from.length === 0 || group.value.to.length === 0) {
        return { required: true };
      } else {
        const start = DateTime.fromFormat(group.value.from, TIME_FORMAT);
        const end = DateTime.fromFormat(group.value.to, TIME_FORMAT);
        const diff = end.diff(start).as('seconds');

        if (diff < 0) {
          const controlStart = (group as FormGroup).get('from');
          const controlEnd = (group as FormGroup).get('to');
          controlEnd?.setErrors({ invalidRange: true });
          controlStart?.setErrors({ invalidRange: true });
        }

        return diff > 0 ? null : { invalidRange: true };
      }
    };
  }
}
